import { useState } from 'react';
import {
  Flex,
  Select,
  Text,
  Tooltip,
  Icon,
  Strong,
  TextField,
  FormField,
} from '@scale/scaleui-radix';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import {
  Config,
  ConfigSchema,
  Metric,
  NGramMetricConfig,
  TopicMetricConfig,
  TypeMetricConfig,
  ComparisonDataset,
  ALL_COMPARISON_DATASETS,
  TemplaticnessSimilarityThresholdValues,
  TemplaticnessSimilarityThreshold,
  TemplaticnessMetricConfig,
} from '@scale/shared/types/quality';
import { assertNever } from '@scale/shared/types/utils';
import _ from 'lodash';

export const parseConfig = (input: unknown): Config => {
  return ConfigSchema.parse(input);
};

function NGramInput({ value, onChange }: { value: number; onChange: (val: number) => void }) {
  const [inputValue, setInputValue] = useState(String(value));

  const handleBlur = () => {
    const parsed = parseInt(inputValue, 10);
    if (!isNaN(parsed)) {
      onChange(parsed);
    } else {
      onChange(value);
      setInputValue(String(value));
    }
  };

  return (
    <TextField.Root
      value={inputValue}
      type="number"
      onChange={e => setInputValue(e.target.value)}
      onBlur={handleBlur}
    />
  );
}

const renderNGramInputs = (
  currentValues: NGramMetricConfig,
  onChange: (values: NGramMetricConfig) => void,
) => {
  return (
    <Flex direction="column" gap="2">
      <Text>
        N-grams analysis identifies repeated word patterns in your prompts. This helps detect
        duplicate content, overused phrases, and ensures prompt diversity by finding sequences of N
        consecutive words that appear frequently. (example: "Consider the math question" is a
        4-gram)
      </Text>

      <Flex direction="row" gap="2" align="center">
        <Text>Minimum N</Text>
        <Tooltip content="Sets the minimum length of word sequences to analyze (e.g., 2 words, 3 words)">
          <Icon label="info" icon={faCircleInfo} color="neutral" />
        </Tooltip>
        <NGramInput
          value={currentValues.metricConfig.minN}
          onChange={val =>
            onChange({
              metric: currentValues.metric,
              metricConfig: { ...currentValues.metricConfig, minN: val },
            })
          }
        />
      </Flex>

      <Flex direction="row" gap="2" align="center">
        <Text>Maximum N</Text>
        <Tooltip content="Sets the maximum length of word sequences to analyze">
          <Icon label="info" icon={faCircleInfo} color="neutral" />
        </Tooltip>
        <NGramInput
          value={currentValues.metricConfig.maxN}
          onChange={val =>
            onChange({
              metric: currentValues.metric,
              metricConfig: {
                ...currentValues.metricConfig,
                maxN: val,
              },
            })
          }
        />
      </Flex>

      <Flex direction="row" gap="2" align="center">
        <Text>Frequency Threshold</Text>
        <Tooltip content="Filters out n-grams that appear less frequently than this threshold to focus on commonly repeated patterns">
          <Icon label="info" icon={faCircleInfo} color="neutral" />
        </Tooltip>
        <NGramInput
          value={currentValues.metricConfig.frequencyThreshold}
          onChange={val =>
            onChange({
              metric: currentValues.metric,
              metricConfig: {
                ...currentValues.metricConfig,
                frequencyThreshold: val,
              },
            })
          }
        />
      </Flex>

      <Text>Note that this analysis will take ~1 minute for every 650 prompts.</Text>
    </Flex>
  );
};

const renderTopicInputs = (
  currentValues: TopicMetricConfig,
  onChange: (values: TopicMetricConfig) => void,
) => {
  return (
    <Flex direction="column" gap="2">
      <Text>
        Prompt topic analysis categorizes prompts into various topics and compares them to a known
        dataset. This helps identify coverage gaps, potential biases, and ensures your prompts
        represent a diverse range of topics.
      </Text>
      <Flex direction="row" gap="2" align="center">
        <Text>Comparison Dataset</Text>
        <Tooltip content="Select a dataset to compare your prompt topics against">
          <Icon label="info" icon={faCircleInfo} color="neutral" />
        </Tooltip>
        <Select.Root
          value={currentValues.metricConfig.comparisonDataset}
          onValueChange={(value: ComparisonDataset) =>
            onChange({
              metric: currentValues.metric,
              metricConfig: {
                ...currentValues.metricConfig,
                comparisonDataset: value,
              },
            })
          }
        >
          <Select.Trigger />
          <Select.Content>
            {ALL_COMPARISON_DATASETS.map(dataset => (
              <Select.Item key={dataset} value={dataset}>
                {dataset}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Flex>
    </Flex>
  );
};

const renderTypeInputs = (
  currentValues: TypeMetricConfig,
  onChange: (values: TypeMetricConfig) => void,
) => {
  return (
    <Flex direction="column" gap="2">
      <Text>
        Prompt type analysis categorizes prompts into different types like Generation, Roleplay,
        Classification, Q&A, Summarization, Extraction, and more. This helps identify what kinds of
        tasks your prompts are being used for and ensures you have appropriate coverage across
        different prompt types.
      </Text>
      <Flex direction="row" gap="2" align="center">
        <Text>Comparison Dataset</Text>
        <Tooltip content="Select a dataset to compare your prompt topics against">
          <Icon label="info" icon={faCircleInfo} color="neutral" />
        </Tooltip>
        <Select.Root
          value={currentValues.metricConfig.comparisonDataset}
          onValueChange={(value: ComparisonDataset) =>
            onChange({
              metric: currentValues.metric,
              metricConfig: {
                ...currentValues.metricConfig,
                comparisonDataset: value,
              },
            })
          }
        >
          <Select.Trigger />
          <Select.Content>
            {ALL_COMPARISON_DATASETS.map(dataset => (
              <Select.Item key={dataset} value={dataset}>
                {dataset}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Flex>
    </Flex>
  );
};

const renderScaleCategoryInputs = (_currentValues: Config, _onChange: (values: Config) => void) => {
  return (
    <Text>
      The Scale category system first groups prompts into top-level functional categories: Coding,
      Math and Reasoning, or Instruction Following, and then groups them into more specific
      categories. This helps you understand the variety of tasks your prompts cover and ensures
      thorough coverage. By analyzing the distribution across categories, you can identify gaps,
      optimize performance, and refine your approach over time.
    </Text>
  );
};

const renderLMSYSCategoryInputs = (_currentValues: Config, _onChange: (values: Config) => void) => {
  const categories = [
    'Math',
    'Instruction Following',
    'Creative Writing',
    'Coding',
    'Hard Prompts (Overall)',
    'Hard Prompts (English)',
    'Longer Query',
    'English',
    'Chinese',
    'French',
    'German',
    'Spanish',
    'Russian',
    'Korean',
  ];

  return (
    <Flex direction="column" gap="2">
      <Text>This categorizes your prompts into one of the following LMSYS categories:</Text>
      <Flex direction="column" pl="2">
        {categories.map(category => (
          <Text key={category}>
            • <Strong>{category}</Strong>
          </Text>
        ))}
      </Flex>
      <Text>
        By analyzing these categories, you can identify the distribution of prompts across different
        language tasks and domains, allowing you to better understand coverage gaps and optimize
        performance.
      </Text>
    </Flex>
  );
};

const renderSteeringTypeInputs = (_currentValues: Config, _onChange: (values: Config) => void) => {
  const steeringTypes = [
    'Word and Sentence Length',
    'Keywords and Vocabulary',
    'Difficulty and Complexity',
    'Literary Style',
    'Tone',
    'Roleplay',
    'Literary Format',
    'Structured Data Output',
    'Negative Constraints',
    'Safety and Sensitivity',
    'Other',
    'None',
  ];
  return (
    <Flex direction="column" gap="2">
      <Text>
        This categorizes your prompts into one of the following constraints-based steering types:
      </Text>
      <Flex direction="column" pl="2">
        {steeringTypes.map(steeringType => (
          <Text key={steeringType}>
            • <Strong>{steeringType}</Strong>
          </Text>
        ))}
      </Flex>
      <Text>
        This helps you identify and analyze how constraints within your prompts can guide or limit
        the model's responses.
      </Text>
    </Flex>
  );
};

const renderDomainDifficultyInputs = (
  _currentValues: Config,
  _onChange: (values: Config) => void,
) => {
  const domainDifficultyCategories = [
    {
      label: 'Ultra Low Domain Difficulty',
      description: 'extremely easy prompts answerable by most people with primary-school education',
    },
    {
      label: 'Low Domain Difficulty',
      description: 'easy prompts requiring high-school education',
    },
    {
      label: 'Medium Domain Difficulty',
      description: 'moderately difficult prompts suited for college-level knowledge',
    },
    {
      label: 'High Domain Difficulty',
      description: 'prompting advanced domain expertise',
    },
    {
      label: 'Ultra High Domain Difficulty',
      description: 'extremely difficult, often Olympiad-level or frontier domain knowledge',
    },
  ];

  return (
    <Flex direction="column" gap="2">
      <Text>
        This categorizes your prompts into one of the following domain difficulty categories:
      </Text>
      <Flex direction="column" pl="2">
        {domainDifficultyCategories.map(category => (
          <Text key={category.label}>
            • <Strong>{category.label}</Strong> ({category.description})
          </Text>
        ))}
      </Flex>
      <Text>
        By classifying your prompts, you can identify and analyze how challenging each is, ensuring
        a clearer understanding of the difficulty involved.
      </Text>
    </Flex>
  );
};

const renderConstraintsDifficultyInputs = (
  _currentValues: Config,
  _onChange: (values: Config) => void,
) => {
  const constraintsDifficultyCategories = [
    {
      label: 'Ultra Low Constraints Difficulty',
      description: 'the prompt has no constraints',
    },
    {
      label: 'Low Constraints Difficulty',
      description: 'the prompt has 1 constraint',
    },
    {
      label: 'Medium Constraints Difficulty',
      description: 'the prompt has 2 constraints',
    },
    {
      label: 'High Constraints Difficulty',
      description: 'the prompt has 3 constraints',
    },
    {
      label: 'Ultra High Constraints Difficulty',
      description: 'the prompt has more than 3 constraints',
    },
  ];
  return (
    <Flex direction="column" gap="2">
      <Text>
        This categorizes your prompts into one of the following constraints difficulty categories,
        based on how many constraints they contain:
      </Text>
      <Flex direction="column" pl="2">
        {constraintsDifficultyCategories.map(category => (
          <Text key={category.label}>
            • <Strong>{category.label}</Strong> ({category.description})
          </Text>
        ))}
      </Flex>
      <Text>
        By understanding and classifying the number of constraints in your prompts, you can better
        evaluate how directive or complex they are, and refine your prompt strategy accordingly.
      </Text>
    </Flex>
  );
};

const renderPromptLengthInputs = (_currentValues: Config, _onChange: (values: Config) => void) => {
  return <Text>This metric categorizes your prompt lengths into buckets</Text>;
};

const renderContrivednessInputs = (_currentValues: Config, _onChange: (values: Config) => void) => {
  return <Text>This metric categorizes your prompts into contrivedness buckets</Text>;
};

const renderHumanlikenessInputs = (_currentValues: Config, _onChange: (values: Config) => void) => {
  return <Text>This metric categorizes your prompts into humanlikeness buckets</Text>;
};

const renderPromptSimilarityInputs = (
  _currentValues: Config,
  _onChange: (values: Config) => void,
) => {
  return <Text>This metric shows your most similar prompts</Text>;
};

const renderTemplaticnessInputs = (
  templaticnessConfig: TemplaticnessMetricConfig,
  onChange: (values: TemplaticnessMetricConfig) => void,
) => {
  return (
    <Flex direction="column" gap="2">
      <Text>This metric shows your most templatic prompts</Text>

      <FormField.Label>Prompt Similarity Threshold</FormField.Label>
      <Select.Root
        value={String(templaticnessConfig.metricConfig.promptSimilarityThreshold)}
        onValueChange={(value: string) =>
          onChange({
            metric: Metric.Templaticness,
            metricConfig: _.assign(_.clone(templaticnessConfig.metricConfig), {
              promptSimilarityThreshold: Number(
                value,
              ) as unknown as TemplaticnessSimilarityThreshold,
            }),
          })
        }
      >
        <Select.Trigger />
        <Select.Content>
          {TemplaticnessSimilarityThresholdValues.map(
            (threshold: TemplaticnessSimilarityThreshold) => (
              <Select.Item key={threshold} value={String(threshold)}>
                {_.round(threshold * 100)}%
              </Select.Item>
            ),
          )}
        </Select.Content>
      </Select.Root>
    </Flex>
  );
};

export const renderConfigInputs = (config: Config, onChange: (values: Config) => void) => {
  switch (config.metric) {
    case Metric.NGrams:
      return renderNGramInputs(config, onChange);
    case Metric.Topic:
      return renderTopicInputs(config, onChange);
    case Metric.Type:
      return renderTypeInputs(config, onChange);
    case Metric.ScaleCategory:
      return renderScaleCategoryInputs(config, onChange);
    case Metric.LmsysCategory:
      return renderLMSYSCategoryInputs(config, onChange);
    case Metric.SteeringType:
      return renderSteeringTypeInputs(config, onChange);
    case Metric.DomainDifficulty:
      return renderDomainDifficultyInputs(config, onChange);
    case Metric.ConstraintsDifficulty:
      return renderConstraintsDifficultyInputs(config, onChange);
    case Metric.PromptLength:
      return renderPromptLengthInputs(config, onChange);
    case Metric.Contrivedness:
      return renderContrivednessInputs(config, onChange);
    case Metric.Humanlikeness:
      return renderHumanlikenessInputs(config, onChange);
    case Metric.PromptSimilarity:
      return renderPromptSimilarityInputs(config, onChange);
    case Metric.Templaticness:
      return renderTemplaticnessInputs(config, onChange);
    default:
      assertNever(config);
  }
};
